/**
 * AdminLogin Component
 *
 * Provides login form for admin panel authentication.
 *
 * Features:
 * - Username and password input fields
 * - Form validation (required fields)
 * - Error display for failed login attempts
 * - Loading state during authentication
 * - Automatic redirect to dashboard on success
 *
 * Authentication Flow:
 * 1. User enters credentials
 * 2. Form validates inputs (both fields required)
 * 3. Submit calls AdminContext.login()
 * 4. Success: Redirects to /admin/dashboard
 * 5. Failure: Displays error message
 *
 * Usage:
 * ```tsx
 * <AdminLogin />
 * ```
 */

import { useAdmin } from '@context/AdminContext';
import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './AdminLogin.module.css';

/**
 * AdminLogin Component
 */
const AdminLogin: React.FC = () => {
  // Auth context
  const { login, isLoading, error: contextError, clearError } = useAdmin();
  const navigate = useNavigate();

  // Form state
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  /**
   * Handle form submission
   * Validates inputs and calls login from AdminContext
   * @param e
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Clear previous errors
    setValidationError('');
    clearError();

    // Validate inputs
    if (!username.trim()) {
      setValidationError('Username is required');
      return;
    }

    if (!password) {
      setValidationError('Password is required');
      return;
    }

    try {
      // Call login from AdminContext
      await login(username, password);

      // On success, navigate to dashboard
      navigate('/admin/dashboard');
    } catch (err) {
      // Error is handled by AdminContext and displayed via contextError
      console.error('Login failed:', err);
    }
  };

  /**
   * Get the error message to display
   * Priority: validation error > context error
   */
  const displayError = validationError || contextError;

  return (
    <div className={styles.container}>
      <div className={styles.loginBox}>
        <h1 className={styles.title}>Admin Login</h1>
        <p className={styles.subtitle}>Portfolio Admin Panel</p>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Username field */}
          <div className={styles.formGroup}>
            <label htmlFor="username" className={styles.label}>
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.input}
              placeholder="Enter username"
              disabled={isLoading}
              autoComplete="username"
              autoFocus
            />
          </div>

          {/* Password field */}
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Enter password"
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          {/* Error message */}
          {displayError ? (
            <div className={styles.error} role="alert">
              {displayError}
            </div>
          ) : null}

          {/* Submit button */}
          <button type="submit" className={styles.submitButton} disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
